import React from 'react'
import { AppTooltip } from '../../../../components/AppTooltip'
import {
  getStatusGlossaryItem,
  getLogoutLogicGlossaryItem,
  SESSION_STATUS_GLOSSARY,
} from '../constants/sessionGlossary'

/**
 * Reusable component for rendering session Status with direct hover tooltip.
 */
export function SessionStatusBadge({ status }) {
  const meta = getStatusGlossaryItem(status)

  const tooltipContent = (
    <div className="space-y-1">
      <p className="font-bold text-on-surface">{meta.label}</p>
      <p className="text-xs text-on-surface-variant leading-snug">{meta.meaning}</p>
      {meta.detail ? (
        <p className="text-[11px] text-on-surface-variant/80 border-t border-outline-variant/20 pt-1 mt-1">
          {meta.detail}
        </p>
      ) : null}
    </div>
  )

  return (
    <AppTooltip content={tooltipContent} side="top" align="start">
      <span
        tabIndex={0}
        className={`inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-[0.08em] cursor-help transition-opacity hover:opacity-85 focus:outline-hidden ${meta.badgeClass}`}
      >
        <span className={`h-2.5 w-2.5 rounded-full ${meta.dotClass}`} />
        {meta.label}
      </span>
    </AppTooltip>
  )
}

/**
 * Reusable component for rendering Logout Logic with badge and direct hover tooltip.
 */
export function LogoutLogicBadge({ reason, isLiveSession = false }) {
  const meta = getLogoutLogicGlossaryItem(reason, isLiveSession)

  const tooltipContent = (
    <div className="space-y-1">
      <p className="font-bold text-on-surface">{meta.label}</p>
      <p className="text-xs text-on-surface-variant leading-snug">{meta.meaning}</p>
      {meta.detail ? (
        <p className="text-[11px] text-on-surface-variant/80 border-t border-outline-variant/20 pt-1 mt-1">
          {meta.detail}
        </p>
      ) : null}
    </div>
  )

  return (
    <AppTooltip content={tooltipContent} side="top" align="start">
      <span
        tabIndex={0}
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.07em] cursor-help transition-all hover:shadow-xs hover:opacity-90 focus:outline-hidden ${meta.badgeClass}`}
      >
        {meta.label}
      </span>
    </AppTooltip>
  )
}

/**
 * Reusable component for rendering Logout Time in the Timeline column.
 * Accurately displays Active Now (ongoing) vs MISSING vs normal timestamp with direct tooltips.
 */
export function TimelineLogoutDisplay({ logoutTime, isLiveSession = false }) {
  const isMissing = logoutTime === 'MISSING' || (!logoutTime && !isLiveSession)
  const isActive = isLiveSession || logoutTime === 'ACTIVE_NOW'

  if (isActive) {
    const activeTooltip = (
      <div className="space-y-1">
        <p className="font-bold text-on-surface">Active Session (Ongoing)</p>
        <p className="text-xs text-on-surface-variant leading-snug">
          Employee is currently active in this session. Logout timestamp will be recorded automatically when the session terminates.
        </p>
      </div>
    )

    return (
      <AppTooltip content={activeTooltip} side="top" align="start">
        <span
          tabIndex={0}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700 cursor-help transition-opacity hover:opacity-90 focus:outline-hidden"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Active Now
        </span>
      </AppTooltip>
    )
  }

  if (isMissing) {
    const missingMeta = SESSION_STATUS_GLOSSARY.MISSING
    const missingTooltip = (
      <div className="space-y-1">
        <p className="font-bold text-error">Missing Logout</p>
        <p className="text-xs text-on-surface-variant leading-snug">
          {missingMeta.meaning}
        </p>
        <p className="text-[11px] text-on-surface-variant/80 border-t border-outline-variant/20 pt-1 mt-1">
          {missingMeta.detail}
        </p>
      </div>
    )

    return (
      <AppTooltip content={missingTooltip} side="top" align="start">
        <span
          tabIndex={0}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-error cursor-help transition-opacity hover:opacity-90 focus:outline-hidden"
        >
          <span className="h-2 w-2 rounded-full bg-error" />
          MISSING
        </span>
      </AppTooltip>
    )
  }

  return (
    <span className="inline-flex items-center gap-2 text-sm font-medium text-on-surface-variant">
      <span className="h-2 w-2 rounded-full bg-outline-variant" />
      {logoutTime}
    </span>
  )
}

